import { useNavigate } from 'react-router-dom';
import LegalModal, { ls } from '../components/LegalModal';

export default function TermsOfService() {
  const navigate = useNavigate();
  const close = () => navigate(-1);

  const sections = [
    {
      heading: 'What GhostLight Is',
      content: <p style={ls.body}>GhostLight is a pseudonymous emotional support and community debrief platform operated by NINJA ("we," "us") for live entertainment professionals. It is not a social network, not a therapy service, and not a crisis intervention tool — though it surfaces crisis resources prominently.</p>,
    },
    {
      heading: 'Pseudonymity, Not Anonymity',
      content: <>
        <p style={{ ...ls.body, marginBottom: 12 }}>GhostLight is pseudonymous. This distinction is critical:</p>
        <ul style={ls.list}>
          <li>Every user registers with a real name and email address. These are never displayed publicly.</li>
          <li>Every user is assigned a persistent alias. This alias is your public identity on the platform.</li>
          <li>All identity data (name, email, IP address at registration, session logs) is stored securely and accessible only to NINJA administrators.</li>
          <li>Moderators see aliases only. They cannot access your real identity.</li>
        </ul>
      </>,
    },
    {
      heading: 'When We May Disclose Your Identity',
      content: <>
        <p style={ls.body}>Identity data is disclosed externally only under:</p>
        <ul style={{ ...ls.list, marginTop: 12 }}>
          <li><strong style={ls.strong}>Valid legal compulsion</strong> — a law enforcement subpoena or court order requiring disclosure.</li>
          <li><strong style={ls.strong}>Credible, specific threat to life</strong> — if we have reasonable grounds to believe there is an imminent risk of serious harm to you or another person.</li>
        </ul>
        <p style={{ ...ls.body, marginTop: 12 }}>We will not disclose identity for any other reason. We will publish an annual transparency report detailing the number of requests received and complied with.</p>
      </>,
    },
    {
      heading: 'Ghost Hours',
      content: <p style={ls.body}>All Ghost Hours conversations are end-to-end encrypted. NINJA administrators cannot read them. The single exception: if a user reports a message from within a Ghost Hours conversation, the reported message is decrypted by the reporter's device and submitted to the moderation team.</p>,
    },
    {
      heading: 'Content and Conduct',
      content: <ul style={ls.list}>
        <li>Be decent to each other. This community runs on mutual respect.</li>
        <li>No harassment, threats, doxxing, or targeted abuse.</li>
        <li>No commercial solicitation, spam, or self-promotion.</li>
        <li>Crisis-related content triggers automatic resource surfacing and moderator review. We do not attempt to intervene clinically.</li>
      </ul>,
    },
    {
      heading: 'Moderation',
      content: <>
        <p style={{ ...ls.body, marginBottom: 12 }}>Moderation is a two-tier system:</p>
        <ul style={ls.list}>
          <li><strong style={ls.strong}>Moderators</strong> handle day-to-day community management: removing content, warnings, temporary suspensions, and locking threads. They see aliases only.</li>
          <li><strong style={ls.strong}>NINJA administrators</strong> handle identity access, legal matters, permanent bans, and escalated situations.</li>
        </ul>
        <p style={{ ...ls.body, marginTop: 12 }}>Permanent bans require agreement of two moderators and are executed by an administrator. All moderation actions are logged with reasons.</p>
      </>,
    },
    {
      heading: 'Data We Collect',
      content: <>
        <ul style={ls.list}>
          <li>Name, email address, and department (at registration)</li>
          <li>IP address at registration</li>
          <li>Session and authentication logs</li>
          <li>Posts, replies, and debrief responses you create</li>
          <li>Aggregated, anonymized Check-In Pulse data</li>
        </ul>
        <p style={{ ...ls.body, marginTop: 12 }}><strong style={ls.strong}>We do not sell data. We do not run advertising. We do not share data with third parties except as required by law.</strong></p>
      </>,
    },
    {
      heading: 'Your Rights',
      content: <>
        <ul style={ls.list}>
          <li>You may request a full export of your data at any time.</li>
          <li>You may request full deletion of your account, alias, and all associated content.</li>
          <li>Session logs are retained for a legal compliance window and then deleted.</li>
          <li>These rights apply regardless of your jurisdiction (GDPR, CCPA, or otherwise).</li>
        </ul>
        <p style={ls.note}>Questions? Contact terrence@ninjasgo.com</p>
      </>,
    },
    {
      heading: 'No Warranty & Changes',
      content: <>
        <p style={ls.body}>GhostLight is provided as-is. We are not a healthcare provider. We do not guarantee the accuracy, reliability, or completeness of any content or resources on the platform. If you are in crisis, please contact a professional service directly.</p>
        <p style={{ ...ls.body, marginTop: 14 }}>We may update these terms. Material changes will be communicated via the platform. Continued use after changes constitutes acceptance.</p>
      </>,
    },
  ];

  return <LegalModal title="Terms of Service" updated="Last updated: April 2026" onClose={close} sections={sections} />;
}
